import { analytics } from "@/lib/analytics";
import type { SwitchTarget } from "@/utils/supplierComparison";
import sdk from "@repo/api-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateCartShippingOptions, invalidateSellerCarts } from "./cache";

/**
 * A switch to perform, as `switchTargetFor` describes it, plus whether it is an
 * undo.
 *
 * Undo is the one thing the matrix knows and the seam cannot infer: it is
 * implemented as a switch back, so without the flag a reverted switch is
 * indistinguishable from a kept one and inflates the pilot's headline number
 * (A -> B -> A reads as two switches when the salon changed nothing).
 */
export type SwitchSupplierInput = SwitchTarget & { isUndo?: boolean };

/**
 * Raised when the add-to-new-supplier step succeeds but the follow-up
 * removal from the old supplier's cart fails. Deliberately not retried or
 * rolled back here (see `useSwitchSupplier` docs) - callers use this to show
 * a specific "check both carts" message instead of a generic failure.
 */
export class PartialSwitchError extends Error {
  constructor(
    message: string,
    public readonly newCartId: string,
    cause?: unknown
  ) {
    super(message, { cause });
    this.name = "PartialSwitchError";
  }
}

/**
 * Switches a product from one supplier's cart to another. There is no
 * combined "move line item" endpoint, so this composes the two SDK calls
 * that do exist: add to the target seller (which resolves or creates their
 * cart from `offer_id` alone), then remove from the source cart.
 *
 * Both calls live in ONE mutation rather than chaining `useAddToSellerCart`
 * and `useRemoveCartItem`. Those each invalidate the seller-cart cache in
 * their own `onSettled`, so chaining them fires two full cart-list refetches
 * per switch instead of one - and the first lands mid-switch, briefly
 * publishing a state where the product sits in both carts at once. One
 * mutation means one invalidation, after both writes have settled.
 *
 * The add runs first on purpose: if the process dies between the two calls,
 * the product is duplicated rather than lost. Deliberately no automatic
 * rollback when the removal fails - retrying the rollback is itself a call
 * that can also fail, and would risk a third, even-less-predictable state.
 * The caller gets a `PartialSwitchError` to surface a "check both carts"
 * message instead.
 *
 * `supplier_switched` is emitted here rather than at the call site, matching
 * `useAddToSellerCart`: this is the only place that knows the switch actually
 * landed, so a failed or partial switch can never be counted as one, and any
 * future surface that offers a switch is instrumented by construction. It is
 * the pilot's money event - the direct evidence that putting suppliers side by
 * side changes what a salon buys - so "wherever it originates" matters more
 * here than anywhere else.
 */
export function useSwitchSupplier() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      offerId,
      quantity,
      sourceCartId,
      lineItemId
    }: SwitchSupplierInput) => {
      const addResult = await sdk.store.sellerCart.addItem({
        offer_id: offerId,
        quantity
      });

      try {
        await sdk.store.sellerCart.deleteLineItem(sourceCartId, lineItemId);
      } catch (removeError) {
        throw new PartialSwitchError(
          "Added to the new supplier but couldn't remove it from the old one.",
          addResult.cart_id,
          removeError
        );
      }

      return addResult;
    },

    // `onSuccess`, not `onSettled`: a switch that threw is not a switch. A
    // PartialSwitchError does not reach here either, which is right - the
    // product ended up in both carts, so nothing was re-sourced.
    onSuccess: (_data, { change, isUndo = false }) => {
      analytics.supplierSwitched({
        product_id: change.productId,
        variant_id: change.variantId,
        from_seller_id: change.fromSellerId,
        to_seller_id: change.toSellerId,
        from_unit_price: change.fromUnitPrice,
        to_unit_price: change.toUnitPrice,
        // Negative means the salon saved money.
        price_delta: change.toUnitPrice - change.fromUnitPrice,
        is_undo: isUndo
      });
    },

    onSettled: (data, error, { sourceCartId }) => {
      // Both ends change contents, so both can change what they're quoted:
      // the source cart shrinks, the target grows (and may be brand new). A
      // partial switch has no `data`, but the add did land and its error
      // carries the target cart's id.
      invalidateCartShippingOptions(queryClient, sourceCartId);
      invalidateCartShippingOptions(
        queryClient,
        data?.cart_id ??
          (error instanceof PartialSwitchError ? error.newCartId : undefined)
      );
      //
      // Returned so the mutation settles only once the carts have refetched:
      // the matrix's Undo reads the post-switch lines off those carts, and the
      // toast offering it appears when the mutation resolves (KAN-260).
      return invalidateSellerCarts(queryClient);
    }
  });

  return {
    switchSupplier: mutation.mutateAsync,
    isPending: mutation.isPending
  };
}
